import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
const today = new Date().toISOString().split("T")[0];
const ago90 = new Date(Date.now() - 90 * 86400000).toISOString().split("T")[0];

export default function StatementsPage() {
  const [type, setType] = useState("customer");
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [dateFrom, setDateFrom] = useState(ago90);
  const [dateTo, setDateTo] = useState(today);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const url = type === "customer" ? "/customers?limit=500" : "/suppliers?limit=500";
    api.get(url).then(r => {
      setEntities(type === "customer" ? r.data.customers || [] : r.data.suppliers || []);
      setEntityId("");
      setStatement(null);
    });
  }, [type]);

  const generate = async () => {
    if (!entityId) { toast.error(`Select a ${type}`); return; }
    setLoading(true);
    try {
      const url = type === "customer" ? `/statements/customer/${entityId}` : `/statements/supplier/${entityId}`;
      const r = await api.get(`${url}?date_from=${dateFrom}&date_to=${dateTo}`);
      setStatement(r.data);
    } catch { toast.error("Failed to generate statement"); }
    setLoading(false);
  };

  const idField = type === "customer" ? "customer_id" : "supplier_id";

  return (
    <div data-testid="statements-page">
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] mb-1">Account Statements</h1>
      <p className="text-sm text-gray-500 mb-6">Generate customer or supplier account statements</p>

      <Card className="border rounded-sm mb-6"><CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="statement-type" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="customer">Customer</SelectItem><SelectItem value="supplier">Supplier</SelectItem></SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">{type === "customer" ? "Customer" : "Supplier"}</label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger data-testid="statement-entity" className="rounded-sm"><SelectValue placeholder={`Select ${type}...`} /></SelectTrigger>
              <SelectContent>{entities.map(e => <SelectItem key={e[idField]} value={e[idField]}>{e.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">From</label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-sm" /></div>
          <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">To</label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-sm" /></div>
          <Button data-testid="generate-statement-btn" onClick={generate} disabled={loading} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}Generate
          </Button>
        </div>
      </CardContent></Card>

      {statement && (
        <div data-testid="statement-result">
          <Card className="border rounded-sm mb-4"><CardContent className="p-5">
            <h2 className="font-heading font-bold text-lg text-[#1F2937] mb-1">{statement.customer_name || statement.supplier_name}</h2>
            <p className="text-sm text-gray-500">Statement: {statement.date_from} to {statement.date_to}</p>
          </CardContent></Card>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="border rounded-sm"><CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Opening Balance</p>
              <p className="text-xl font-bold font-mono text-[#1F2937]">{fmt(statement.opening_balance)}</p>
            </CardContent></Card>
            <Card className="border rounded-sm"><CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{type === "customer" ? "Invoices" : "Expenses"}</p>
              <p className="text-xl font-bold font-mono text-red-500">{fmt(statement.total_invoices || statement.total_expenses)}</p>
            </CardContent></Card>
            <Card className="border rounded-sm"><CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Payments</p>
              <p className="text-xl font-bold font-mono text-green-600">({fmt(statement.total_payments)})</p>
            </CardContent></Card>
            <Card className="border rounded-sm border-[#E63946]/30"><CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Closing Balance</p>
              <p className="text-xl font-bold font-mono text-[#E63946]">{fmt(statement.closing_balance)}</p>
            </CardContent></Card>
          </div>

          <Card className="border rounded-sm"><div className="overflow-x-auto">
            <table data-testid="statement-transactions" className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Reference</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Description</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Debit</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Credit</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Balance</th>
              </tr></thead>
              <tbody>
                <tr className="border-b bg-gray-50/30"><td className="p-3 text-gray-500" colSpan="6">Opening Balance</td><td className="p-3 text-right font-mono font-medium">{fmt(statement.opening_balance)}</td></tr>
                {(statement.transactions || []).map((t, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="p-3 text-gray-600">{t.date}</td>
                    <td className="p-3">
                      <Badge className={`rounded-full text-xs ${t.type === "Payment" || t.type === "Credit Note" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                        {t.type === "Payment" || t.type === "Credit Note" ? <ArrowDownRight className="w-3 h-3 mr-0.5 inline" /> : <ArrowUpRight className="w-3 h-3 mr-0.5 inline" />}{t.type}
                      </Badge>
                    </td>
                    <td className="p-3 font-medium text-[#E63946]">{t.reference}</td>
                    <td className="p-3 text-gray-600 max-w-[200px] truncate">{t.description}</td>
                    <td className="p-3 text-right font-mono">{t.debit > 0 ? fmt(t.debit) : ""}</td>
                    <td className="p-3 text-right font-mono text-green-600">{t.credit > 0 ? `(${fmt(t.credit)})` : ""}</td>
                    <td className="p-3 text-right font-mono font-medium">{fmt(t.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t-2 font-bold">
                <td className="p-3" colSpan="4">Closing Balance</td>
                <td className="p-3 text-right font-mono text-red-500">{fmt(statement.total_invoices || statement.total_expenses)}</td>
                <td className="p-3 text-right font-mono text-green-600">({fmt(statement.total_payments)})</td>
                <td className="p-3 text-right font-mono text-[#E63946]">{fmt(statement.closing_balance)}</td>
              </tr></tfoot>
            </table>
          </div></Card>
        </div>
      )}
    </div>
  );
}
